import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PencilIcon,
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Bars3Icon
} from '@heroicons/react/24/outline';
import { useCourses } from '../../contexts/CourseContext';
import { supabase } from '../../lib/supabase';
import { adminUpdateApplicationStatus, adminConfirmPayment } from '../../services/adminApplicationService';
import { ProgramClass } from '../../types';
import {
  getProgramClasses,
  deleteProgramClass,
  getBatchClassApplicationStats,
  ClassApplicationStats,
  updateClassesOrder,
} from '../../services/programClassService';
import {
  adminApproveRefund,
  adminRejectRefund,
  adminCompleteRefund,
  getRefundStatusLabel,
  getRefundStatusColor,
  Refund,
} from '../../services/refundService';
import ClassForm from './ClassForm';

interface ApplicationWithStudent {
  id: string;
  application_number: string;
  student_name: string;
  status: 'registered' | 'waiting' | 'cancelled';
  waiting_number: number | null;
  created_at: string;
  cancelled_at: string | null;
  applicant_type: 'self' | 'child';
  class_id: string;
  // 결제 관련 필드
  payment_status: 'pending' | 'completed' | 'expired' | 'cancelled' | 'refunded' | null;
  payment_amount: number | null;
  payment_deadline: string | null;
  paid_at: string | null;
  // 환불 관련 필드
  refund?: Refund;
}

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getCourse } = useCourses();
  const [programClasses, setProgramClasses] = useState<ProgramClass[]>([]);
  const [classStats, setClassStats] = useState<Record<string, ClassApplicationStats>>({});
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [applications, setApplications] = useState<ApplicationWithStudent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'all' | 'registered' | 'waiting' | 'cancelled'>('all');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [confirmingPaymentId, setConfirmingPaymentId] = useState<string | null>(null);
  const [showClassForm, setShowClassForm] = useState(false);
  const [editingClass, setEditingClass] = useState<ProgramClass | null>(null);
  const [copyingClass, setCopyingClass] = useState<ProgramClass | null>(null);
  // 환불 처리 관련 state
  const [processingRefundId, setProcessingRefundId] = useState<string | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingRefund, setRejectingRefund] = useState<{ id: string; studentName: string } | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  // 순서 변경 모드 관련 state
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [originalOrder, setOriginalOrder] = useState<ProgramClass[]>([]);
  const [savingOrder, setSavingOrder] = useState(false);

  const course = getCourse(id || '');

  useEffect(() => {
    if (id) {
      loadProgramClasses();
    }
  }, [id]);

  useEffect(() => {
    if (selectedClassId) {
      loadApplications();
    }
  }, [selectedClassId]);

  const loadProgramClasses = async () => {
    setLoading(true);
    try {
      const classes = await getProgramClasses(id!);
      setProgramClasses(classes);

      if (classes.length > 0) {
        // 첫 번째 반 선택
        setSelectedClassId(classes[0].id);

        // 모든 반의 신청 현황 로드
        const classIds = classes.map(c => c.id);
        const stats = await getBatchClassApplicationStats(classIds);
        setClassStats(stats);
      }
    } catch (error) {
      console.error('Error loading program classes:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadApplications = async () => {
    if (!selectedClassId) return;

    try {
      // 1. applications 조회
      const { data: apps, error } = await supabase
        .from('applications')
        .select('*')
        .eq('class_id', selectedClassId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading applications:', error);
        return;
      }

      if (!apps || apps.length === 0) {
        setApplications([]);
        return;
      }

      // 2. student_id 목록으로 profiles에서 username 조회
      const studentIds = Array.from(new Set(apps.map(a => a.student_id).filter(Boolean)));
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username')
        .in('id', studentIds);

      // 3. username 매핑
      const usernameMap: Record<string, string> = {};
      profiles?.forEach(p => {
        usernameMap[p.id] = p.username;
      });

      // 4. 환불 정보 조회
      const applicationIds = apps.map(a => a.id);
      const { data: refunds } = await supabase
        .from('refunds')
        .select('*')
        .in('application_id', applicationIds);

      // 5. 환불 매핑
      const refundMap: Record<string, Refund> = {};
      refunds?.forEach(r => {
        refundMap[r.application_id] = r as Refund;
      });

      // 6. applications에 username과 refund 추가
      const appsWithData = apps.map(app => ({
        ...app,
        username: usernameMap[app.student_id] || '-',
        refund: refundMap[app.id]
      }));

      setApplications(appsWithData);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleStatusChange = async (
    applicationId: string,
    newStatus: 'registered' | 'waiting' | 'cancelled'
  ) => {
    if (processingId) return;

    // 현재 신청 정보 찾기
    const application = applications.find(a => a.id === applicationId);
    const currentStatus = application?.status;

    // 상태 전이별 확인 메시지
    let confirmMessage = '';
    if (currentStatus === 'waiting' && newStatus === 'registered') {
      confirmMessage = `"${application?.student_name}"님을 수강 확정하시겠습니까?\n\n⚠️ 정원을 초과할 수 있습니다.`;
    } else if (currentStatus === 'registered' && newStatus === 'cancelled') {
      confirmMessage = `"${application?.student_name}"님의 수강을 취소하시겠습니까?\n\n※ 대기 1번이 자동으로 수강 확정됩니다.`;
    } else if (currentStatus === 'waiting' && newStatus === 'cancelled') {
      confirmMessage = `"${application?.student_name}"님의 대기를 취소하시겠습니까?`;
    } else {
      confirmMessage = '상태를 변경하시겠습니까?';
    }

    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) return;

    setProcessingId(applicationId);

    try {
      const response = await adminUpdateApplicationStatus({
        application_id: applicationId,
        new_status: newStatus,
      });

      if (response.success) {
        alert(
          response.data?.promoted
            ? `상태가 변경되었습니다.\n대기자 ${response.data.promoted.student_name}님이 자동으로 등록되었습니다.`
            : '상태가 변경되었습니다.'
        );
        // 목록 새로고침
        await loadApplications();
        // 통계 새로고침
        if (selectedClassId) {
          const stats = await getBatchClassApplicationStats([selectedClassId]);
          setClassStats(prev => ({ ...prev, ...stats }));
        }
      } else {
        alert(`오류: ${response.error?.message || '상태 변경에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('상태 변경 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const handleAddClass = () => {
    setEditingClass(null);
    setCopyingClass(null);
    setShowClassForm(true);
  };

  const handleEditClass = (programClass: ProgramClass) => {
    setEditingClass(programClass);
    setCopyingClass(null);
    setShowClassForm(true);
  };

  const handleCopyClass = (programClass: ProgramClass) => {
    // 복사할 데이터 준비 - 반 이름에 "(복사)" 추가
    const copiedClass: ProgramClass = {
      ...programClass,
      class_name: `${programClass.class_name} (복사)`,
    };
    setEditingClass(null);
    setCopyingClass(copiedClass);
    setShowClassForm(true);
  };

  const handleDeleteClass = async (classId: string, className: string) => {
    const confirmed = window.confirm(
      `정말로 "${className}" 반을 삭제하시겠습니까?\n\n⚠️ 주의: 신청자가 있는 반은 삭제할 수 없습니다.`
    );

    if (!confirmed) return;

    try {
      await deleteProgramClass(classId);
      alert('반이 삭제되었습니다.');
      await loadProgramClasses();
    } catch (error: any) {
      console.error('Error deleting class:', error);
      alert(error.message || '반 삭제 중 오류가 발생했습니다.');
    }
  };

  const handleClassFormSuccess = async () => {
    await loadProgramClasses();
  };

  // 순서 변경 모드 시작
  const startReorderMode = () => {
    setOriginalOrder([...programClasses]);
    setIsReorderMode(true);
  };

  // 순서 변경 취소
  const cancelReorderMode = () => {
    setProgramClasses(originalOrder);
    setIsReorderMode(false);
    setOriginalOrder([]);
  };

  // 순서 변경 저장
  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      const orderedClasses = programClasses.map((c, i) => ({
        id: c.id,
        display_order: i,
      }));
      await updateClassesOrder(orderedClasses);
      setIsReorderMode(false);
      setOriginalOrder([]);
      alert('순서가 저장되었습니다.');
    } catch (error) {
      console.error('Error saving class order:', error);
      alert('순서 저장에 실패했습니다.');
    } finally {
      setSavingOrder(false);
    }
  };

  // 반 순서 변경 (UI만)
  const handleMoveClass = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= programClasses.length) return;

    const newClasses = [...programClasses];
    const temp = newClasses[index];
    newClasses[index] = newClasses[newIndex];
    newClasses[newIndex] = temp;

    setProgramClasses(newClasses);
  };

  // 환불 승인 처리
  const handleApproveRefund = async (refundId: string, studentName: string) => {
    if (processingRefundId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 환불을 승인하시겠습니까?\n\n승인 후 실제 환불 처리가 완료되면 '환불 완료' 처리를 해주세요.`
    );

    if (!confirmed) return;

    setProcessingRefundId(refundId);

    try {
      const result = await adminApproveRefund(refundId);

      if (result.success) {
        alert('환불이 승인되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '환불 승인에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error approving refund:', error);
      alert('환불 승인 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 환불 거절 모달 열기
  const openRejectModal = (refundId: string, studentName: string) => {
    setRejectingRefund({ id: refundId, studentName });
    setRejectionReason('');
    setShowRejectModal(true);
  };

  // 환불 거절 처리
  const handleRejectRefund = async () => {
    if (!rejectingRefund || !rejectionReason.trim()) {
      alert('거절 사유를 입력해주세요.');
      return;
    }

    setProcessingRefundId(rejectingRefund.id);

    try {
      const result = await adminRejectRefund(rejectingRefund.id, rejectionReason);

      if (result.success) {
        alert('환불이 거절되었습니다.');
        setShowRejectModal(false);
        setRejectingRefund(null);
        setRejectionReason('');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '환불 거절에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error rejecting refund:', error);
      alert('환불 거절 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 환불 완료 처리
  const handleCompleteRefund = async (refundId: string, studentName: string) => {
    if (processingRefundId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 환불 완료 처리를 하시겠습니까?\n\n⚠️ 실제 환불 송금이 완료된 후에 처리해주세요.\n- 신청 상태가 '취소'로 변경됩니다.\n- 결제 상태가 '환불'로 변경됩니다.\n- 대기자가 있으면 자동으로 수강 확정됩니다.`
    );

    if (!confirmed) return;

    setProcessingRefundId(refundId);

    try {
      const result = await adminCompleteRefund(refundId);

      if (result.success) {
        alert('환불 완료 처리되었습니다.');
        await loadApplications();
        // 통계 새로고침
        if (selectedClassId) {
          const stats = await getBatchClassApplicationStats([selectedClassId]);
          setClassStats(prev => ({ ...prev, ...stats }));
        }
      } else {
        alert(`오류: ${result.error?.message || '환불 완료 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error completing refund:', error);
      alert('환불 완료 처리 중 오류가 발생했습니다.');
    } finally {
      setProcessingRefundId(null);
    }
  };

  // 환불 상태 뱃지
  const getRefundBadge = (refund: Refund | undefined) => {
    if (!refund) return null;

    return (
      <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${getRefundStatusColor(refund.refund_status)}`}>
        {getRefundStatusLabel(refund.refund_status)}
      </span>
    );
  };

  // 입금확인 처리
  const handleConfirmPayment = async (applicationId: string, studentName: string) => {
    if (confirmingPaymentId) return;

    const confirmed = window.confirm(
      `"${studentName}"님의 입금을 확인 처리하시겠습니까?\n\n결제 상태가 '완료'로 변경됩니다.`
    );

    if (!confirmed) return;

    setConfirmingPaymentId(applicationId);

    try {
      const result = await adminConfirmPayment(applicationId);

      if (result.success) {
        alert('입금 확인이 완료되었습니다.');
        await loadApplications();
      } else {
        alert(`오류: ${result.error?.message || '입금확인 처리에 실패했습니다.'}`);
      }
    } catch (error) {
      console.error('Error confirming payment:', error);
      alert('입금확인 처리 중 오류가 발생했습니다.');
    } finally {
      setConfirmingPaymentId(null);
    }
  };

  // 결제 상태 뱃지
  const getPaymentStatusBadge = (paymentStatus: string | null, paymentDeadline: string | null) => {
    if (!paymentStatus) {
      return <span className="text-xs text-gray-400">-</span>;
    }

    // 기한 만료 여부 체크
    const isExpired = paymentDeadline && new Date(paymentDeadline) < new Date();

    switch (paymentStatus) {
      case 'completed':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-emerald-50 text-emerald-600 border border-emerald-200">결제완료</span>;
      case 'pending':
        return (
          <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${
            isExpired
              ? 'bg-red-50 text-red-600 border border-red-200'
              : 'bg-amber-50 text-amber-600 border border-amber-200'
          }`}>
            {isExpired ? '기한초과' : '입금대기'}
          </span>
        );
      case 'expired':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-red-50 text-red-600 border border-red-200">만료</span>;
      case 'cancelled':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200">취소</span>;
      case 'refunded':
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-purple-50 text-purple-600 border border-purple-200">환불</span>;
      default:
        return <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200">{paymentStatus}</span>;
    }
  };

  if (!course) {
    return (
      <div className="p-8">
        <p className="text-sm text-gray-600 mb-6">강의를 찾을 수 없습니다.</p>
        <button
          onClick={() => navigate('/admin/courses')}
          className="inline-flex items-center px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-150"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          강의 목록으로 돌아가기
        </button>
      </div>
    );
  }

  const selectedClass = programClasses.find(c => c.id === selectedClassId);
  const selectedStats = selectedClassId ? classStats[selectedClassId] : null;

  // 필터링된 신청자 목록
  const filteredApplications = statusFilter === 'all'
    ? applications
    : applications.filter(app => app.status === statusFilter);

  // 반의 모집 상태를 계산
  const getClassRecruitmentStatus = (programClass: ProgramClass): 'recruiting' | 'upcoming' | 'completed' => {
    const now = new Date();
    const startTime = programClass.registration_start_time?.substring(0, 5) || '00:00';
    const endTime = programClass.registration_end_time?.substring(0, 5) || '23:59';
    const startDateTime = new Date(`${programClass.registration_start_date}T${startTime}:00+09:00`);
    const endDateTime = new Date(`${programClass.registration_end_date}T${endTime}:00+09:00`);
    const courseEndDate = new Date(programClass.course_end_date);

    if (now > courseEndDate) return 'completed';
    if (now >= startDateTime && now <= endDateTime) return 'recruiting';
    if (now < startDateTime) return 'upcoming';
    return 'completed';
  };

  const getRecruitmentStatusBadge = (status: 'recruiting' | 'upcoming' | 'completed') => {
    switch (status) {
      case 'recruiting':
        return <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">모집중</span>;
      case 'upcoming':
        return <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-600 border border-amber-200">모집예정</span>;
      case 'completed':
        return <span className="inline-flex px-3 py-1 text-xs font-medium rounded-full bg-slate-50 text-slate-600 border border-slate-200">종료</span>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'registered':
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-emerald-50 text-emerald-600 border border-emerald-200 whitespace-nowrap">접수완료</span>;
      case 'waiting':
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">대기중</span>;
      case 'cancelled':
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200 whitespace-nowrap">취소</span>;
      default:
        return <span className="inline-block px-2.5 py-1 text-xs font-medium rounded bg-slate-50 text-slate-600 border border-slate-200 whitespace-nowrap">{status}</span>;
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-full mx-auto">
      {/* 헤더 */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => navigate('/admin/courses')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-150"
              title="목록으로 돌아가기"
            >
              <ArrowLeftIcon className="h-5 w-5 text-gray-700" />
            </button>
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">{course.courseName}</h1>
              <p className="text-sm text-gray-500 mt-1">강의 상세 및 반 관리</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`/admin/courses/edit/${id}`)}
            className="flex items-center px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-150"
          >
            <PencilIcon className="h-4 w-4 mr-2" />
            강의 정보 수정
          </button>
        </div>
      </div>

      {/* 반 관리 섹션 */}
      <div className="bg-white border border-gray-200 rounded-lg mb-6">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">반 목록</h2>
            <div className="flex items-center gap-2">
              {isReorderMode ? (
                <>
                  <button
                    onClick={cancelReorderMode}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150"
                  >
                    취소
                  </button>
                  <button
                    onClick={saveOrder}
                    disabled={savingOrder}
                    className="flex items-center px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150 disabled:opacity-50"
                  >
                    {savingOrder ? '저장 중...' : '순서 저장'}
                  </button>
                </>
              ) : (
                <>
                  {programClasses.length > 1 && (
                    <button
                      onClick={startReorderMode}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors duration-150"
                    >
                      <Bars3Icon className="h-4 w-4 mr-2" />
                      순서 변경
                    </button>
                  )}
                  <button
                    onClick={handleAddClass}
                    className="flex items-center px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors duration-150"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    반 추가
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500">로딩 중...</div>
        ) : programClasses.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-600 mb-6">아직 등록된 반이 없습니다.</p>
            <button
              onClick={handleAddClass}
              className="inline-flex items-center px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-150"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              첫 번째 반 추가하기
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {programClasses.map((programClass, index) => {
              const stats = classStats[programClass.id];
              const isSelected = selectedClassId === programClass.id;
              const recruitmentStatus = getClassRecruitmentStatus(programClass);
              const isFirst = index === 0;
              const isLast = index === programClasses.length - 1;
              return (
                <div
                  key={programClass.id}
                  className={`border rounded-lg p-4 transition-all duration-150 ${
                    isReorderMode
                      ? 'border-blue-300 bg-blue-50'
                      : isSelected
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex gap-4">
                    {/* 순서 변경 버튼 - 순서 변경 모드일 때만 표시 */}
                    {isReorderMode && (
                      <div className="flex flex-col justify-center gap-1">
                        <button
                          onClick={() => handleMoveClass(index, 'up')}
                          disabled={isFirst}
                          className={`p-1.5 rounded transition-colors ${
                            isFirst
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
                          }`}
                          title="위로 이동"
                        >
                          <ChevronUpIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleMoveClass(index, 'down')}
                          disabled={isLast}
                          className={`p-1.5 rounded transition-colors ${
                            isLast
                              ? 'text-gray-300 cursor-not-allowed'
                              : 'text-blue-600 hover:text-blue-800 hover:bg-blue-100'
                          }`}
                          title="아래로 이동"
                        >
                          <ChevronDownIcon className="h-5 w-5" />
                        </button>
                      </div>
                    )}

                  <div className="flex flex-col gap-4 flex-1">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {programClass.class_name}
                        </h3>
                        {getRecruitmentStatusBadge(recruitmentStatus)}
                      </div>
                      <div className="grid grid-cols-1 gap-2 text-sm">
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">정원:</span>
                          <div className="flex items-center flex-wrap gap-2">
                            <span className="text-gray-900 font-bold whitespace-nowrap">{stats?.registered_count || 0}/{programClass.max_students}명</span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                              stats && stats.waiting_count > 0
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-gray-50 text-gray-500 border border-gray-200'
                            }`}>
                              대기 {stats?.waiting_count || 0}/{programClass.waiting_list || 0}명
                            </span>
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">모집기간:</span>
                          <span className="text-gray-900 text-sm">{programClass.registration_start_date} ~ {programClass.registration_end_date}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">교육기간:</span>
                          <span className="text-gray-900 text-sm">{programClass.course_start_date} ~ {programClass.course_end_date}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">수업시간:</span>
                          <span className="text-gray-900 text-sm">{programClass.course_days?.join(', ')} {programClass.course_start_time}~{programClass.course_end_time}</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">수강료:</span>
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="text-gray-900 font-bold whitespace-nowrap">{programClass.tuition.toLocaleString()}원</span>
                            {programClass.material_fee > 0 && (
                              <span className="text-gray-700 text-sm whitespace-nowrap">+ 재료비 {programClass.material_fee.toLocaleString()}원</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center min-w-0">
                          <span className="text-gray-700 font-medium sm:w-24 flex-shrink-0">공개상태:</span>
                          <span className={`font-bold whitespace-nowrap ${programClass.is_published ? 'text-emerald-600' : 'text-red-600'}`}>
                            {programClass.is_published ? '✅ 공개중' : '❌ 비공개'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                      <button
                        onClick={() => setSelectedClassId(programClass.id)}
                        className={`flex items-center px-4 py-2 text-sm rounded-lg font-medium transition-all duration-150 whitespace-nowrap ${
                          isSelected
                            ? 'bg-indigo-600 text-white shadow-md hover:bg-indigo-700'
                            : 'bg-white text-gray-700 border border-gray-200 hover:border-indigo-500 hover:text-indigo-600'
                        }`}
                      >
                        <UserGroupIcon className="h-4 w-4 mr-1.5" />
                        신청자 보기
                      </button>
                      <button
                        onClick={() => handleEditClass(programClass)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-150 border border-gray-200"
                      >
                        <PencilIcon className="h-4 w-4 mr-1.5" />
                        수정
                      </button>
                      <button
                        onClick={() => handleCopyClass(programClass)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-150 border border-gray-200"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-1.5" />
                        복사
                      </button>
                      <button
                        onClick={() => handleDeleteClass(programClass.id, programClass.class_name)}
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-150 border border-gray-200"
                      >
                        <TrashIcon className="h-4 w-4 mr-1.5" />
                        삭제
                      </button>
                    </div>
                  </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 신청자 목록 (반이 선택된 경우만 표시) */}
      {selectedClass && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">
              신청자 관리 - <span className="text-indigo-600">{selectedClass.class_name}</span>
            </h2>

            {/* 통계 */}
            {selectedStats && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-emerald-50 border border-emerald-200 p-4 rounded-lg">
                  <div className="text-sm text-emerald-700 font-medium mb-1">접수완료</div>
                  <div className="text-2xl font-semibold text-emerald-700">
                    {selectedStats.registered_count}명
                  </div>
                </div>
                <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                  <div className="text-sm text-amber-700 font-medium mb-1">대기중</div>
                  <div className="text-2xl font-semibold text-amber-700">
                    {selectedStats.waiting_count}명
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 p-4 rounded-lg">
                  <div className="text-sm text-slate-700 font-medium mb-1">취소</div>
                  <div className="text-2xl font-semibold text-slate-600">
                    {selectedStats.cancelled_count}명
                  </div>
                </div>
              </div>
            )}

            {/* 상태 필터 */}
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setStatusFilter('all')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'all'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                전체
              </button>
              <button
                onClick={() => setStatusFilter('registered')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'registered'
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                접수완료
              </button>
              <button
                onClick={() => setStatusFilter('waiting')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'waiting'
                    ? 'bg-amber-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                대기중
              </button>
              <button
                onClick={() => setStatusFilter('cancelled')}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all duration-150 whitespace-nowrap ${
                  statusFilter === 'cancelled'
                    ? 'bg-slate-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                취소
              </button>
            </div>
          </div>

          {/* 신청자 테이블 */}
          <div className="overflow-x-auto">
            {filteredApplications.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-gray-500">
                신청자가 없습니다.
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      신청번호
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      아이디
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      수강생
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      신청상태
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      결제상태
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      환불상태
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      결제금액
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      결제기한
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      대기
                    </th>
                    <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">
                      신청일시
                    </th>
                    <th className="px-3 py-2.5 text-center text-xs font-semibold text-gray-600 whitespace-nowrap">
                      작업
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((application: any) => {
                    const isCancelled = application.status === 'cancelled';
                    return (
                      <tr
                        key={application.id}
                        className={`transition-colors duration-150 ${isCancelled ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                      >
                        <td className={`px-3 py-2.5 text-sm font-medium whitespace-nowrap ${isCancelled ? 'text-gray-400' : 'text-gray-900'}`}>
                          {application.application_number}
                        </td>
                        <td className={`px-3 py-2.5 text-sm whitespace-nowrap ${isCancelled ? 'text-gray-400' : 'text-gray-600'}`}>
                          {application.username || '-'}
                        </td>
                        <td className="px-3 py-2.5 text-sm whitespace-nowrap">
                          <div className={`font-medium ${isCancelled ? 'text-gray-400' : 'text-gray-900'}`}>
                            {application.student_name}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {getStatusBadge(application.status)}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {getPaymentStatusBadge(application.payment_status, application.payment_deadline)}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {getRefundBadge(application.refund) || <span className="text-xs text-gray-400">-</span>}
                        </td>
                        <td className={`px-3 py-2.5 text-sm whitespace-nowrap ${isCancelled ? 'text-gray-400' : 'text-gray-600'}`}>
                          {application.payment_amount
                            ? `${application.payment_amount.toLocaleString()}원`
                            : '-'}
                        </td>
                        <td className={`px-3 py-2.5 text-sm whitespace-nowrap ${isCancelled ? 'text-gray-400' : 'text-gray-600'}`}>
                          {application.payment_deadline
                            ? new Date(application.payment_deadline).toLocaleDateString('ko-KR')
                            : '-'}
                        </td>
                        <td className={`px-3 py-2.5 text-sm whitespace-nowrap ${isCancelled ? 'text-gray-400' : 'text-gray-600'}`}>
                          {application.waiting_number || '-'}
                        </td>
                        <td className={`px-3 py-2.5 text-sm whitespace-nowrap ${isCancelled ? 'text-gray-400' : 'text-gray-600'}`}>
                          {new Date(application.created_at).toLocaleDateString('ko-KR')}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="flex justify-center gap-2 flex-wrap">
                            {/* 입금확인 버튼: registered + pending일 때만 */}
                            {application.status === 'registered' &&
                              (application.payment_status === 'pending' || application.payment_status === 'expired') && (
                              <button
                                onClick={() => handleConfirmPayment(application.id, application.student_name)}
                                disabled={confirmingPaymentId === application.id}
                                className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {confirmingPaymentId === application.id ? '처리중...' : '입금확인'}
                              </button>
                            )}

                            {/* 환불 신청 상태: 승인/거절 버튼 */}
                            {application.refund?.refund_status === 'requested' && (
                              <>
                                <button
                                  onClick={() => handleApproveRefund(application.refund!.id, application.student_name)}
                                  disabled={processingRefundId === application.refund!.id}
                                  className="px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {processingRefundId === application.refund!.id ? '처리중...' : '환불승인'}
                                </button>
                                <button
                                  onClick={() => openRejectModal(application.refund!.id, application.student_name)}
                                  disabled={processingRefundId === application.refund!.id}
                                  className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  환불거절
                                </button>
                              </>
                            )}

                            {/* 환불 승인 상태: 완료 버튼 */}
                            {application.refund?.refund_status === 'approved' && (
                              <button
                                onClick={() => handleCompleteRefund(application.refund!.id, application.student_name)}
                                disabled={processingRefundId === application.refund!.id}
                                className="px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {processingRefundId === application.refund!.id ? '처리중...' : '환불완료'}
                              </button>
                            )}

                            {/* 접수완료 상태: 수강 취소 버튼 (환불 진행중이 아닐 때만) */}
                            {application.status === 'registered' && !application.refund && (
                              <button
                                onClick={() => handleStatusChange(application.id, 'cancelled')}
                                disabled={processingId === application.id}
                                className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {processingId === application.id ? '처리중...' : '수강 취소'}
                              </button>
                            )}
                            {/* 대기중 상태: 수강 확정 + 대기 취소 버튼 */}
                            {application.status === 'waiting' && (
                              <>
                                <button
                                  onClick={() => handleStatusChange(application.id, 'registered')}
                                  disabled={processingId === application.id}
                                  className="px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-md hover:bg-emerald-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {processingId === application.id ? '처리중...' : '수강 확정'}
                                </button>
                                <button
                                  onClick={() => handleStatusChange(application.id, 'cancelled')}
                                  disabled={processingId === application.id}
                                  className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {processingId === application.id ? '처리중...' : '대기 취소'}
                                </button>
                              </>
                            )}
                            {/* 취소 상태: 버튼 없음 (환불 완료 포함) */}
                            {application.status === 'cancelled' && !application.refund && (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                            {/* 환불 완료/취소 상태: 버튼 없음 */}
                            {(application.refund?.refund_status === 'completed' || application.refund?.refund_status === 'cancelled') && (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* ClassForm 모달 */}
      {showClassForm && (
        <ClassForm
          programId={id!}
          programName={course.courseName}
          classToEdit={editingClass}
          initialData={copyingClass}
          courseTuition={0}
          courseMaterialFee={0}
          courseSelectionMethod={course.selectionMethod}
          onClose={() => {
            setShowClassForm(false);
            setEditingClass(null);
            setCopyingClass(null);
          }}
          onSuccess={handleClassFormSuccess}
        />
      )}

      {/* 환불 거절 모달 */}
      {showRejectModal && rejectingRefund && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">환불 거절</h3>
              <p className="text-sm text-gray-500 mt-1">
                {rejectingRefund.studentName}님의 환불 신청을 거절합니다.
              </p>
            </div>
            <div className="px-6 py-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                거절 사유 <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="거절 사유를 입력해주세요..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={4}
              />
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingRefund(null);
                  setRejectionReason('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleRejectRefund}
                disabled={processingRefundId === rejectingRefund.id || !rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingRefundId === rejectingRefund.id ? '처리 중...' : '거절하기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseDetail;
